import { Avatar, Box, Button, Chip, Skeleton, Typography } from '@mui/material';
import { ReactQueryErrorWrapper } from 'components/shared/react-query-error-wrapper';
import { useFetchArticle } from 'components/blog/fetch-hooks';
import { ContentBlock } from 'components/blog/content-blocks';
import { Calendar } from 'lucide-react';
import dayjs from 'dayjs';
import { useParams, useHistory } from 'react-router-dom';

export const ArticleDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const history = useHistory();
  const articleQuery = useFetchArticle(slug);

  const isLoading = articleQuery.isLoading;
  const isSuccess = articleQuery.isSuccess;
  const article = articleQuery.data?.data[0];

  if (isLoading) {
    return (
      <Box
        sx={{
          maxWidth: '900px',
          margin: '0 auto',
          padding: '24px',
        }}
      >
        <Skeleton variant="rectangular" height="400px" sx={{ marginBottom: '24px', borderRadius: '16px' }} />
        <Skeleton variant="text" height="60px" sx={{ marginBottom: '16px' }} />
        <Skeleton variant="text" height="200px" />
      </Box>
    );
  }

  if (!article) {
    return (
      <Box
        sx={{
          maxWidth: '900px',
          margin: '0 auto',
          padding: '24px',
        }}
      >
        <Typography variant="h4">Article not found</Typography>
      </Box>
    );
  }

  const dateToDisplay = article.displayPublishDate || article.publishedAt;
  const formattedDate = dayjs(dateToDisplay).format('MMMM DD, YYYY');

  return (
    <>
      <ReactQueryErrorWrapper queries={[articleQuery]} mutations={[]} />

      {/* Back Button - positioned at top right */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          paddingX: '24px',
          paddingTop: '24px',
        }}
      >
        <Button
          variant="contained"
          onClick={() => history.goBack()}
          sx={{
            textTransform: 'uppercase',
            fontSize: '13px',
            fontWeight: 500,
            paddingX: '10px',
            paddingY: '4px',
          }}
        >
          Back
        </Button>
      </Box>

      <Box
        sx={{
          maxWidth: '900px',
          margin: '0 auto',
          padding: '24px',
        }}
      >
        {isSuccess && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Cover Image */}
          {article.cover && (
            <Box
              component="img"
              src={article.cover.url}
              alt={article.cover.alternativeText || article.title}
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: '500px',
                objectFit: 'cover',
                borderRadius: '16px',
              }}
            />
          )}

          {/* Date and Category */}
          <Box
            sx={{
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Calendar size={20} />
              <Typography variant="body2" color="text.secondary">
                {formattedDate}
              </Typography>
            </Box>
            {article.category && (
              <Chip
                label={article.category.name}
                variant="outlined"
                color="primary"
                size="small"
              />
            )}
          </Box>

          {/* Title */}
          <Typography variant="h3" fontWeight={700}>
            {article.title}
          </Typography>

          {/* Description */}
          <Typography variant="h6" color="text.secondary">
            {article.description}
          </Typography>

          {/* Author */}
          {article.author && (
            <Box
              sx={{
                display: 'flex',
                gap: '12px',
                alignItems: 'center',
                paddingY: '16px',
                borderTop: '1px solid',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Avatar
                src={article.author.avatar?.url}
                alt={article.author.name}
                sx={{ width: 56, height: 56 }}
              />
              <Box>
                <Typography variant="h6">{article.author.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {article.author.title}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Content Blocks */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            {article.blocks.map((block, index) => (
              <ContentBlock key={index} block={block} index={index} />
            ))}
          </Box>
        </Box>
      )}
      </Box>
    </>
  );
};
